// src/hooks/queries/useConfiguracion.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { configuracionService } from '@/services/configuracionService';
import type {
  Perfil,
  PerfilUpdate,
  CambiarPassword,
  UsuarioAdmin,
  UsuarioCreate,
  UsuarioUpdate,
  RolAdmin,
  RolCreate,
  TipoTicketConfig,
  TipoActividadConfig,
  CategoriaConocimientoConfig,
  ModuloConfig,
  ModuloCreate,
  ModuloUpdate,
} from '@/types';

// ============ Perfil ============

export function usePerfil() {
  return useQuery<Perfil>({
    queryKey: ['auth', 'me'],
    queryFn: () => configuracionService.obtenerPerfil(),
  });
}

export function useActualizarPerfil() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PerfilUpdate) => configuracionService.actualizarPerfil(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useCambiarPassword() {
  return useMutation({
    mutationFn: (data: CambiarPassword) => configuracionService.cambiarPassword(data),
  });
}

export function useCambiarAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => configuracionService.cambiarAvatar(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

// ============ Usuarios ============

export function useUsuarios(params?: { tipo?: string; activo?: boolean; search?: string }) {
  return useQuery<UsuarioAdmin[]>({
    queryKey: ['usuarios', params],
    queryFn: () => configuracionService.listarUsuarios(params),
  });
}

export function useUsuario(id: string) {
  return useQuery<UsuarioAdmin>({
    queryKey: ['usuarios', id],
    queryFn: () => configuracionService.obtenerUsuario(id),
    enabled: !!id,
  });
}

export function useCrearUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UsuarioCreate) => configuracionService.crearUsuario(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    },
  });
}

export function useActualizarUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UsuarioUpdate }) =>
      configuracionService.actualizarUsuario(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      queryClient.invalidateQueries({ queryKey: ['usuarios', id] });
    },
  });
}

export function useEliminarUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => configuracionService.eliminarUsuario(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    },
  });
}

export function useResetearPassword() {
  return useMutation({
    mutationFn: (id: string) => configuracionService.resetearPassword(id),
  });
}

// ============ Roles ============

export function useRoles() {
  return useQuery<RolAdmin[]>({
    queryKey: ['roles'],
    queryFn: () => configuracionService.listarRoles(),
  });
}

export function useRol(id: string) {
  return useQuery<RolAdmin>({
    queryKey: ['roles', id],
    queryFn: () => configuracionService.obtenerRol(id),
    enabled: !!id,
  });
}

export function useCrearRol() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RolCreate) => configuracionService.crearRol(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

export function useActualizarRol() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RolCreate> }) =>
      configuracionService.actualizarRol(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['roles', id] });
    },
  });
}

export function useEliminarRol() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => configuracionService.eliminarRol(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

export function usePermisos() {
  return useQuery<string[]>({
    queryKey: ['permisos'],
    queryFn: () => configuracionService.listarPermisos(),
  });
}

// ============ Tipos de Ticket ============

export function useTiposTicket() {
  return useQuery<TipoTicketConfig[]>({
    queryKey: ['config', 'tipos-ticket'],
    queryFn: () => configuracionService.listarTiposTicket(),
  });
}

export function useActualizarTipoTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TipoTicketConfig> }) =>
      configuracionService.actualizarTipoTicket(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config', 'tipos-ticket'] });
    },
  });
}

// ============ Tipos de Actividad ============

export function useTiposActividad() {
  return useQuery<TipoActividadConfig[]>({
    queryKey: ['config', 'tipos-actividad'],
    queryFn: () => configuracionService.listarTiposActividad(),
  });
}

export function useActualizarTipoActividad() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TipoActividadConfig> }) =>
      configuracionService.actualizarTipoActividad(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config', 'tipos-actividad'] });
    },
  });
}

// ============ Categorías de Conocimiento ============

export function useCategoriasConocimiento() {
  return useQuery<CategoriaConocimientoConfig[]>({
    queryKey: ['config', 'categorias-conocimiento'],
    queryFn: () => configuracionService.listarCategoriasConocimiento(),
  });
}

export function useActualizarCategoriaConocimiento() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CategoriaConocimientoConfig> }) =>
      configuracionService.actualizarCategoriaConocimiento(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config', 'categorias-conocimiento'] });
    },
  });
}

// ============ Módulos ============

export function useModulosConfig() {
  return useQuery<ModuloConfig[]>({
    queryKey: ['config', 'modulos'],
    queryFn: () => configuracionService.listarModulos(false),
  });
}

export function useCrearModulo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ModuloCreate) => configuracionService.crearModulo(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config', 'modulos'] });
      queryClient.invalidateQueries({ queryKey: ['actividades', 'catalogos', 'modulos'] });
    },
  });
}

export function useActualizarModulo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ModuloUpdate }) =>
      configuracionService.actualizarModulo(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config', 'modulos'] });
      queryClient.invalidateQueries({ queryKey: ['actividades', 'catalogos', 'modulos'] });
    },
  });
}

export function useDesactivarModulo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => configuracionService.desactivarModulo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config', 'modulos'] });
      queryClient.invalidateQueries({ queryKey: ['actividades', 'catalogos', 'modulos'] });
    },
  });
}
